import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QuestionsList from '../../../Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL;

// Circular progress component
const CircularProgress = ({ percentage, size = 80, showLabel = true }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const getProgressColor = (percent) => {
    if (percent >= 100) return '#198754';
    if (percent >= 75) return '#0d6efd';
    if (percent >= 50) return '#ffc107';
    if (percent >= 25) return '#fd7e14';
    return '#dc3545';
  };

  const progressColor = getProgressColor(percentage);

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-bg"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-bar"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showLabel && (
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        )}
      </svg>
    </div>
  );
};

const NewAuditSections = () => {
  const [sectionNames, setSectionNames] = useState([]);
  const [subsubsections, setSubsubsections] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [answers, setAnswers] = useState({});
  const [dataTable, setDataTable] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    unansweredQuestions: 0,
    totalPoints: 0,
    maxPossiblePoints: 0,
    percentage: 0
  });
  const [showNext, setShowNext] = useState(false);
  const [currentLevel, setCurrentLevel] = useState('subsection'); // Start with subsections
  const [sectionData, setSectionData] = useState(null);
  const [sectionProgress, setSectionProgress] = useState(null);
  const [itemsProgress, setItemsProgress] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  
  // Add new state for reorganized data
  const [reorganizedData, setReorganizedData] = useState([]);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { checklist } = location.state || {};
  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id || null;

  const notify = (message, type = 'error') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  // Fetch and reorganize data to group by subsections (previously lines)
  useEffect(() => {
    const fetchAndReorganizeData = async () => {
      if (!checklist?.checklist_id || !checklist?.BranchCode) {
        notify("Missing checklist information");
        setLoading(false);
        return;
      }

      try {
        // First get all sections using existing endpoint
        const sectionsResponse = await fetch(
          `${BASE_URL}/get-checklist-sections/${checklist.checklist_id}/${checklist.BranchCode}`
        );

        if (!sectionsResponse.ok) {
          throw new Error(`Failed to fetch sections: ${sectionsResponse.status}`);
        }

        const sectionsData = await sectionsResponse.json();
        if (!sectionsData.sections) {
          throw new Error('No sections data in response');
        }

        setSectionNames(sectionsData.sections);

        // Reorganize data by grouping subsubsections under subsections
        const subsectionGroups = {};
        
        for (const sectionName of sectionsData.sections) {
          try {
            // Get section details
            const sectionResponse = await fetch(
              `${BASE_URL}/get-section-details/${checklist.checklist_id}/${checklist.BranchCode}/${sectionName}`
            );

            if (sectionResponse.ok) {
              const sectionData = await sectionResponse.json();
              
              if (sectionData.type === 'subsections' || sectionData.type === 'both') {
                const subsectionsToCheck = sectionData.type === 'both' ? 
                  sectionData.subsections : sectionData.data;

                // For each subsection, check for subsubsections
                for (const subsection of subsectionsToCheck) {
                  try {
                    const subsectionPath = `${sectionName}|${subsection.name}`;
                    const subsectionResponse = await fetch(
                      `${BASE_URL}/get-nested-section-details/${checklist.checklist_id}/${checklist.BranchCode}/${subsectionPath}`
                    );

                    if (subsectionResponse.ok) {
                      const subsectionData = await subsectionResponse.json();
                      
                      if (subsectionData.type === 'subsections' || subsectionData.type === 'both') {
                        const subsubsectionsToAdd = subsectionData.type === 'both' ? 
                          subsectionData.subsections : subsectionData.data;

                        subsubsectionsToAdd.forEach(subsubsection => {
                          const subsectionName = subsubsection.name; // Sub Section 01, Sub Section 02, etc.
                          
                          if (!subsectionGroups[subsectionName]) {
                            subsectionGroups[subsectionName] = {
                              name: subsectionName,
                              subsubsections: []
                            };
                          }
                          
                          // Add subsubsection to this subsection if not already present
                          if (!subsectionGroups[subsectionName].subsubsections.find(subSub => 
                            subSub.name === subsection.name && subSub.section === sectionName)) {
                            subsectionGroups[subsectionName].subsubsections.push({
                              name: subsection.name, 
                              section: sectionName,
                              originalPath: [sectionName, subsection.name, subsubsection.name]
                            });
                          }
                        });
                      }
                    }
                  } catch (error) {
                    console.warn(`Error checking subsection ${subsection.name}:`, error);
                  }
                }
              }
            }
          } catch (error) {
            console.warn(`Error checking section ${sectionName}:`, error);
          }
        }

        // Convert to array and sort subsections
        const reorganizedArray = Object.values(subsectionGroups).sort((a, b) => {
          // Sort subsections: Sub Section 01, Sub Section 02, etc.
          const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
          const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
          return aNum - bNum;
        });

        // Keep subsubsections in their natural order (no forced sorting)
        // The subsubsections will appear in the order they were found in the data

        setReorganizedData(reorganizedArray);
        setIsViewOnly(checklist.status === 'Completed');
        
      } catch (error) {
        console.error('Fetch error:', error);
        notify(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAndReorganizeData();
  }, [checklist]);

  // Fetch progress for reorganized items
  const fetchReorganizedProgress = async () => {
    try {
      const progressMap = {};

      for (const subsection of reorganizedData) {
        // Calculate progress for entire subsection
        let totalQuestions = 0;
        let answeredQuestions = 0;

        for (const subsubsection of subsection.subsubsections) {
          const progress = await fetchItemProgress(subsubsection.originalPath);
          if (progress) {
            const pathKey = subsubsection.originalPath.join('|');
            progressMap[pathKey] = progress;
            
            totalQuestions += progress.totalQuestions || 0;
            answeredQuestions += progress.answeredQuestions || 0;
          }
        }

        // Store subsection-level progress
        progressMap[subsection.name] = {
          totalQuestions,
          answeredQuestions,
          progressPercentage: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
        };
      }

      setItemsProgress(progressMap);
    } catch (error) {
      console.error('Error fetching reorganized progress:', error);
      notify('Failed to load progress data');
    }
  };

  useEffect(() => {
    if (reorganizedData.length > 0) {
      fetchReorganizedProgress();
    }
  }, [reorganizedData]);

  // Fetch progress for items
  const fetchItemProgress = async (path = []) => {
    try {
      const pathParam = path.length > 0 ? path.join('|') : '';
      const response = await fetch(
        `${BASE_URL}/get-section-progress/${checklist.checklist_id}/${checklist.BranchCode}/${auditorData.id}/${checklist.branch_id}${pathParam ? `/${pathParam}` : ''}`
      );

      if (!response.ok) throw new Error('Failed to fetch progress');
      
      const data = await response.json();
      setSectionProgress(data);
      return data;
    } catch (error) {
      console.error('Error:', error);
      notify('Failed to load progress data', 'error');
      return null;
    }
  };

  // Load saved answers
  const loadSavedAnswers = async (sectionName) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-answers/${checklist.checklist_id}/${checklist.id}/${checklist.branch_id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch saved answers');
      }
      
      const data = await response.json();
      
      const relevantAnswers = sectionName 
        ? Object.fromEntries(
            Object.entries(data.data).filter(([_, answer]) => 
              answer.section === sectionName
            )
          )
        : data.data;

      setAnswers(prev => ({
        ...prev,
        ...relevantAnswers
      }));
      
      const answeredCount = Object.values(relevantAnswers).filter(a => a.answer).length;
      setDataTable(prev => ({
        ...prev,
        answeredQuestions: answeredCount,
        unansweredQuestions: questions.length - answeredCount
      }));

    } catch (error) {
      console.error('Error loading saved answers:', error);
      notify('Failed to load saved answers');
    }
  };

  useEffect(() => {
    if (checklist?.checklist_id && checklist?.id && checklist?.branch_id) {
      loadSavedAnswers();
    }
  }, [checklist?.checklist_id, checklist?.id, checklist?.branch_id]);

  // Handle subsection click (first level)
  const handleSubsectionClick = async (subsectionItem) => {
    setSelectedItem(subsectionItem);
    setCurrentPath([subsectionItem.name]);
    setCurrentLevel('subsection');
    
    // Set subsubsections for this subsection
    setSubsections(subsectionItem.subsubsections);
    setQuestions([]);
    setSubsubsections([]);
  };

  // Handle subsubsection click (second level)
  const handleSubsubsectionClick = async (subsubsection) => {
    setIsSectionLoading(true);
    const newPath = [selectedItem.name, subsubsection.name];
    setCurrentPath(newPath);
    setCurrentLevel('subsubsection');

    try {
      // Use the original path to fetch data
      const pathParam = subsubsection.originalPath.join('|');
      const response = await fetch(
        `${BASE_URL}/get-nested-section-details/${checklist.checklist_id}/${checklist.BranchCode}/${pathParam}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subsubsection details');
      }

      const data = await response.json();
      setSectionData(data);
      
      if (data.type === 'questions' || data.type === 'both') {
        setQuestions(data.type === 'both' ? data.questions : data.data);
        setShowNext(false);
      } else {
        setQuestions([]);
        notify('No questions found for this subsubsection', 'info');
      }

      await loadSavedAnswers(subsubsection.originalPath[0]);
      await fetchItemProgress(subsubsection.originalPath);
    } catch (error) {
      console.error('Error:', error);
      notify(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  // Navigation functions
  const navigateToPath = (index) => {
    if (index === 0) {
      // Return to subsections
      resetNavigation();
    } else if (index === 1) {
      // Return to subsubsections within selected subsection
      const subsectionItem = reorganizedData.find(subsection => subsection.name === currentPath[0]);
      if (subsectionItem) {
        handleSubsectionClick(subsectionItem);
      }
    }
  };

  const resetNavigation = () => {
    setSelectedItem(null);
    setSubsections([]);
    setQuestions([]);
    setSubsubsections([]);
    setCurrentPath([]);
    setCurrentLevel('subsection');
  };

  // Update handleAnswerChange to use original path structure
  const handleAnswerChange = (questionId, changes) => {
    // Find the subsubsection info to get original path
    const currentSubsubsection = subsections.find(subSub => subSub.name === currentPath[1]);
    const originalPath = currentSubsubsection?.originalPath || currentPath;

    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...changes,
        section: originalPath[0],
        subsection: originalPath[1],
        subsubsection: originalPath[2]
      }
    }));

    setDataTable(prev => {
      const answeredCount = Object.values(answers).filter(a => a.answer).length;
      return {
        ...prev,
        answeredQuestions: answeredCount,
        unansweredQuestions: prev.totalQuestions - answeredCount
      };
    });
  };

  // Save functionality
  const toastConfig = {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  const handleSubmit = async (shouldNavigate = false) => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      const answersToSubmit = [];

      Object.entries(answers).forEach(([questionId, answerData]) => {
        if (!answerData?.answer?.trim()) return;

        const answerPayload = {
          questionId,
          section: currentPath[0],
          subsection: currentPath.length > 1 ? currentPath[1] : undefined,
          subsubsection: currentPath.length > 2 ? currentPath[2] : undefined,
          answer: answerData.answer,
          remarks: answerData.remarks || '',
          points: answerData.points || 0,
          cap: answerData.cap,
          mediaFileNames: []
        };

        if (answerData.mediaFiles?.length > 0) {
          answerData.mediaFiles.forEach(file => {
            if (file.file instanceof File) {
              formData.append('media', file.file);
              answerPayload.mediaFileNames.push(file.file.name);
            } else if (file.filename) {
              answerPayload.mediaFileNames.push(file.filename);
            }
          });
        } else {
          answerPayload.mediaFileNames = [];
          answerPayload.clearMedia = true;
        }

        if (answerData.deletedFiles?.length > 0) {
          answerPayload.deletedFiles = answerData.deletedFiles;
        }

        answersToSubmit.push(answerPayload);
      });

      formData.append('answers', JSON.stringify(answersToSubmit));
      formData.append('checklistId', checklist.checklist_id);
      formData.append('auditorId', auditorId);
      formData.append('branchId', checklist.branch_id);

      const response = await fetch(`${BASE_URL}/save-answers`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save answers');
      }

      if (shouldNavigate) {
        toast.success("Answers saved successfully!", {
          ...toastConfig,
          onClose: () => navigate('/AuditDetails', { 
            state: { 
              checklist,
              from: 'new-sections',
              sectionPath: currentPath
            }
          })
        });
      } else {
        toast.success("Progress saved successfully!", toastConfig);
      }

    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message, toastConfig);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProgress = () => {
    handleSubmit(false);
  };

  const handleSaveAndContinue = () => {
    handleSubmit(true);
  };


  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Audit Items</p>
          <p className="text-muted">Please wait while we fetch your data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className={`py-4 ${isViewOnly ? 'view-only' : ''}`}>
      <ToastContainer />
      
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-2">{checklist.checklist_name}</h3>
              {isViewOnly && (
                <div className="alert alert-info py-2 px-3 mb-2" style={{ fontSize: '0.875rem' }}>
                  <i className="fas fa-eye me-2"></i>
                  This checklist is completed and in view-only mode
                </div>
              )}
            </div>
            {isViewOnly && (
              <Badge bg="success" className="px-3 py-2">
                <i className="fas fa-check-circle me-1"></i>
                Completed
              </Badge>
            )}
          </div>
          
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  resetNavigation();
                }}>Sub Sections</a>
              </li>
              
              {currentPath.length >= 1 && (
                <li className={`breadcrumb-item ${currentLevel === 'subsection' && currentPath.length === 1 ? 'active' : ''}`}>
                  {currentLevel === 'subsection' && currentPath.length === 1 ? (
                    <span>{currentPath[0]}</span>
                  ) : (
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      navigateToPath(1);
                    }}>{currentPath[0]}</a>
                  )}
                </li>
              )}
              
              {currentPath.length >= 2 && (
                <li className="breadcrumb-item active">
                  <span>{currentPath[1]}</span>
                </li>
              )}
              
              {questions.length > 0 && currentPath.length > 0 && (
                <li className="breadcrumb-item active">Questions</li>
              )}
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Sub Sections Grid (Level 1) */}
      {currentPath.length === 0 && (
        <Row xs={1} md={2} lg={3} className="g-4">
          {reorganizedData.map((subsection, index) => {
            const progress = itemsProgress[subsection.name] || {
              progressPercentage: 0,
              answeredQuestions: 0,
              totalQuestions: 0
            };

            return (
              <Col key={index}>
                <Card 
                  className="h-100 shadow-sm hover-shadow"
                  onClick={() => handleSubsectionClick(subsection)}
                  role="button"
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="text-center mb-3">
                    
                      <CircularProgress 
                        percentage={progress.progressPercentage}
                        size={80}
                        showLabel={true}
                      />
                    </div>
                    <div className="text-center mb-3">
                      <h5 className="mb-1">{subsection.name}</h5>
                      <small className="text-muted d-block">
                        {progress.totalQuestions} Questions
                      </small>
                    </div>
                    <div className="mt-auto">
                      <div className="d-flex justify-content-center gap-3 mb-2">
                        <div className="text-success">
                          <small>{progress.answeredQuestions} Answered</small>
                        </div>
                        <div className="text-danger">
                          <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                        </div>
                      </div>
                     
                      <div className="text-center">
                        <small className={`badge bg-${
                          progress.progressPercentage === 100 ? 'success' :
                          progress.progressPercentage > 0 ? 'warning' : 'danger'
                        }`}>
                          {progress.progressPercentage === 100 ? 'Completed' :
                          progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* SubSub Sections Grid (Level 2) */}
      {subsections.length > 0 && !questions.length && (
        <Row xs={1} md={2} lg={3} className="g-4">
          {subsections.map((subsubsection, index) => {
            const pathKey = subsubsection.originalPath.join('|');
            const progress = itemsProgress[pathKey] || {
              progressPercentage: 0,
              answeredQuestions: 0,
              totalQuestions: 0
            };

            return (
              <Col key={index}>
                <Card 
                  className="h-100 shadow-sm hover-shadow"
                  onClick={() => handleSubsubsectionClick(subsubsection)}
                  role="button"
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="text-center mb-3">
                      
                      <CircularProgress 
                        percentage={progress.progressPercentage} 
                        size={80}
                        showLabel={true}
                      />
                    </div>
                    <div className="text-center mb-3">
                      <h5 className="mb-1">{subsubsection.name}</h5>
                     
                      <small className="text-muted d-block">
                        {progress.totalQuestions} Questions
                      </small>
                    </div>
                    <div className="mt-auto">
                      <div className="d-flex justify-content-center gap-3 mb-2">
                        <div className="text-success">
                          <small>{progress.answeredQuestions} Answered</small>
                        </div>
                        <div className="text-danger">
                          <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <small className={`badge bg-${
                          progress.progressPercentage === 100 ? 'success' :
                          progress.progressPercentage > 0 ? 'warning' : 'danger'
                        }`}>
                          {progress.progressPercentage === 100 ? 'Completed' :
                          progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Questions List (Level 3) */}
      {questions.length > 0 && (
        <QuestionsList
          questions={questions}
          answers={answers}
          handleAnswerChange={handleAnswerChange}
          setAnswers={setAnswers}
          selectedSection={currentPath[currentPath.length - 1]}
          handleSubmit={!isViewOnly ? handleSaveAndContinue : undefined}
          checklist={checklist}
          auditorId={auditorId}
          isViewOnly={isViewOnly}
        />
      )}

      {/* No content message */}
      {!questions.length && !subsections.length && currentPath.length > 0 && (
        <div className="text-center py-5">
          <i className="fas fa-folder-open fa-4x text-muted mb-3"></i>
          <h4 className="text-muted">No content found</h4>
          <Button 
            variant="outline-primary" 
            className="mt-3"
            onClick={resetNavigation}
          >
            Return to Sub Sections
          </Button>
        </div>
      )}

      {/* Navigation Buttons */}
      {(questions.length > 0 || subsections.length > 0) && (
        <div className="navigation-buttons d-flex justify-content-between mt-4"
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1rem',
          right: '1rem',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        }}
        >
          <Button 
            variant="outline-secondary"
            onClick={() => {
              if (currentPath.length <= 1) {
                resetNavigation();
              } else {
                navigateToPath(1);
              }
            }}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back
          </Button>

          <div className="d-flex gap-2">
            {questions.length > 0 && !isViewOnly && (
              <>
                <Button 
                  variant="primary"
                  onClick={handleSaveProgress}
                  disabled={isSaving}
                  className="d-flex align-items-center"
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save
                    </>
                  )}
                </Button>

                <Button 
                  variant="success"
                  onClick={handleSaveAndContinue}
                  disabled={isSaving}
                  className="d-flex align-items-center"
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                    <i className="fas fa-check me-2"></i>
                      Continue
                      
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Styles from original component */}
      <style>
        {`
          .hover-shadow:hover {
            transform: translateY(-5px);
            cursor: pointer;
            transition: transform 0.2s ease-in-out;
          }
          .subsection-icon, .subsubsection-icon {
            width: 50px;
            height: 50px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(13, 110, 253, 0.1);
          }
          .subsubsection-icon {
            background-color: rgba(23, 162, 184, 0.1);
          }
          .breadcrumb-item a {
            text-decoration: none;
            color: #0d6efd;
          }
          .breadcrumb-item a:hover {
            text-decoration: underline;
          }
          .circular-progress {
            position: relative;
            margin-left: 1rem;
          }
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          .card:hover .circular-progress circle.progress-bar {
            filter: brightness(0.9);
          }
          .border-top {
            border-top: 1px solid rgba(0,0,0,.125) !important;
          }
          .card {
            transition: all 0.3s ease;
          }
          .card:hover {
            transform: translateY(-5px);
            box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
          }
          .progress-text-large {
            font-size: 24px;
            font-weight: 700;
            transition: fill 0.35s;
          }
          .progress-text-small {
            font-size: 12px;
            fill: #6c757d;
          }
          .badge {
            font-weight: 500;
            padding: 0.5em 1em;
          }
          .navigation-buttons {
            position: sticky;
            bottom: 1rem;
            z-index: 100;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          @media (max-width: 576px) {
            .navigation-buttons {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              border-radius: 0;
              border-top: 1px solid rgba(0,0,0,0.1);
            }
          }
          .spinner-grow {
            animation-duration: 1.5s;
          }
          .text-primary.fw-bold {
            font-size: 1.2rem;
          }
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          .loading-fadeout {
            animation: fadeOut 0.5s ease-out forwards;
          }
          .view-only input,
          .view-only textarea,
          .view-only select,
          .view-only .form-control {
            pointer-events: none;
            background-color: #f8f9fa !important;
            border-color: #dee2e6 !important;
            cursor: not-allowed;
          }
          .view-only .btn-primary,
          .view-only .btn-success,
          .view-only .btn-info {
            display: none;
          }
          .view-only .file-upload-area,
          .view-only .file-upload-button {
            pointer-events: none;
            opacity: 0.6;
            background-color: #f8f9fa !important;
          }
          .view-only .btn-outline-danger {
            display: none;
          }
          .view-only .form-check-input {
            pointer-events: none;
            background-color: #f8f9fa !important;
          }
          .view-only .form-range {
            pointer-events: none;
            opacity: 0.6;
          }
          .view-only .image-preview .btn {
            display: none;
          }
          .view-only .media-preview .btn {
            display: none;
          }
          .view-only .remarks-section textarea {
            pointer-events: none;
            background-color: #f8f9fa !important;
          }
          .view-only .points-display {
            opacity: 0.8;
          }
          .view-only .navigation-buttons .btn-outline-secondary {
            pointer-events: auto;
            opacity: 1;
            background-color: transparent !important;
          }
          .alert-info {
            border-left: 4px solid #0dcaf0;
            background-color: #cff4fc;
            border-color: #b8e6f0;
            color: #055160;
          }
          .view-only .progress-text {
            opacity: 0.8;
          }
          .view-only .card {
            background-color: #f8f9fa;
            border-color: #dee2e6;
          }
          .view-only .section-progress {
            opacity: 0.8;
          }
          .view-only .hover-shadow:hover {
            transform: none !important;
            box-shadow: 0 .125rem .25rem rgba(0,0,0,.075) !important;
          }
          .view-only .card:hover {
            transform: none !important;
            box-shadow: 0 .125rem .25rem rgba(0,0,0,.075) !important;
          }
          .badge.bg-success {
            font-size: 0.875rem;
            padding: 0.5rem 1rem;
          }


        `}
      </style>
    </Container>
  );
};

export default NewAuditSections;
